import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FileKind, FileTypeService } from '../../../services/attachments/file-type.service';

type ThumbnailStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Presentational file thumbnail, factored out of the Jobsite Gallery markup so
 * the Documents widget renders the exact same visual treatment instead of
 * duplicating it:
 *
 *  - images  → the real preview (lazy loaded, skeleton while it arrives)
 *  - videos  → a poster frame from the video itself + play badge
 *  - folders → the gallery folder tile
 *  - anything else (pdf / word / excel / ppt / text / zip / …) → a file-type
 *    tile with a material icon and the extension label
 *
 * Any failure — missing URL, expired presigned link, permission-restricted
 * file, codec the browser can't decode — falls back to the file-type tile, so
 * a broken-image glyph can never reach the UI.
 */
@Component({
  selector: 'app-file-thumbnail',
  templateUrl: './file-thumbnail.component.html',
  styleUrls: ['./file-thumbnail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FileThumbnailComponent implements OnChanges {
  /** Preview URL for images (thumbnail when the API has one, else the file). */
  @Input() src: string | null | undefined = '';
  /** Source used to grab a poster frame for videos. */
  @Input() videoSrc: string | null | undefined = '';
  @Input() fileName: string = '';
  @Input() fileType: string = '';
  @Input() mimeType: string = '';
  /** Item type as it comes from the API ('file' | 'folder'). */
  @Input() itemType: string = 'file';
  /** `md` = grid tile, `sm` = compact list/table cell. */
  @Input() size: 'sm' | 'md' = 'md';
  /** Show the extension caption under the icon (grid only by default). */
  @Input() showExtension: boolean = true;
  /** Skip lazy loading for above-the-fold tiles. */
  @Input() eager: boolean = false;
  /**
   * Set by the host once it has a genuine generated preview for a kind that
   * isn't natively previewable (e.g. a PDF's page-1 render from
   * ThumbnailGeneratorService). Without this, `src` for a non-image/video
   * kind is assumed to be just the raw file URL — not renderable in an
   * `<img>` — and the file-type tile is shown instead.
   */
  @Input() hasThumbnail: boolean = false;

  kind: FileKind = 'other';
  extension: string = '';
  icon: string = 'insert_drive_file';
  status: ThumbnailStatus = 'idle';

  constructor(
    private fileTypeService: FileTypeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const item = {
      type: this.itemType,
      file_type: this.fileType,
      mime_type: this.mimeType,
      original_name: this.fileName,
    };

    this.kind = this.fileTypeService.kindOf(item);
    this.extension = this.fileTypeService.extensionOf(item);
    this.icon = this.fileTypeService.iconFor(this.kind);

    // Only restart the load cycle when the media source actually changed;
    // re-rendering a row (sort, select, rename) must not re-fetch the image.
    if (changes['src'] || changes['videoSrc'] || changes['fileType'] || changes['itemType'] || changes['hasThumbnail']) {
      this.status = this.hasMedia ? 'loading' : 'idle';
    }
  }

  /** True when we have something real to paint rather than just an icon. */
  get hasMedia(): boolean {
    if (this.kind === 'image') return !!this.src;
    if (this.kind === 'video') return !!(this.videoSrc || this.src);
    if (this.hasThumbnail) return !!this.src;
    return false;
  }

  get showImage(): boolean {
    if (this.status === 'error') return false;
    if (this.kind === 'image') return !!this.src;
    return this.hasThumbnail && !!this.src;
  }

  get showVideo(): boolean {
    return this.kind === 'video' && !!(this.videoSrc || this.src) && this.status !== 'error';
  }

  /** The file-type tile is the fallback for everything else. */
  get showFallbackTile(): boolean {
    return !this.showImage && !this.showVideo && this.kind !== 'folder';
  }

  get showSkeleton(): boolean {
    return this.status === 'loading' && (this.showImage || this.showVideo);
  }

  get mediaSource(): string {
    return this.videoSrc || this.src || '';
  }

  onLoaded(): void {
    if (this.status === 'loaded') return;
    this.status = 'loaded';
    this.cdr.markForCheck();
  }

  onFailed(): void {
    if (this.status === 'error') return;
    this.status = 'error';
    this.cdr.markForCheck();
  }
}
